/* ==========================================================================
   VOLLEYCHAMP theme.js
   Small, dependency-free web components. Each component is self-contained
   and only activates when its custom element is on the page.
   ========================================================================== */
(() => {
  'use strict';

  const theme = window.theme || {};

  /* ---------- Helpers ---------- */

  const formatMoney = (cents, format = theme.moneyFormat || '{{amount}}') => {
    const value = Number(cents) / 100;
    const withDelimiters = (number, decimals, thousands, decimal) => {
      const [int, frac] = number.toFixed(decimals).split('.');
      const intWithSep = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? `${intWithSep}${decimal}${frac}` : intWithSep;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return withDelimiters(value, 0, ',', '.');
        case 'amount_with_comma_separator': return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelimiters(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return withDelimiters(value, 2, "'", '.');
        default: return withDelimiters(value, 2, ',', '.');
      }
    });
  };

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  const lockScroll = (lock) => document.documentElement.classList.toggle('has-drawer', lock);

  /* ---------- Cart API ---------- */

  const Cart = {
    sectionsToRender() {
      const ids = new Set(['cart-drawer']);
      document.querySelectorAll('vc-cart-items[data-section-id]').forEach((el) => ids.add(el.dataset.sectionId));
      return [...ids];
    },

    async request(url, body) {
      const response = await fetch(`${url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections: this.sectionsToRender(), sections_url: window.location.pathname })
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || theme.strings.cartError);
      }
      return data;
    },

    add(items) { return this.request(theme.routes.cartAdd, { items }); },
    change(line, quantity) { return this.request(theme.routes.cartChange, { line, quantity }); },

    /** Replace every cart-related region on the page with fresh server HTML. */
    render(sections) {
      if (!sections) return;
      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        const doc = parseHTML(html);
        document.querySelectorAll(`[data-cart-region="${id}"]`).forEach((target) => {
          const source = doc.querySelector(`[data-cart-region="${id}"]`);
          if (source) target.innerHTML = source.innerHTML;
        });
        const countSource = doc.querySelector('[data-cart-count]');
        if (countSource) {
          document.querySelectorAll('[data-cart-count]').forEach((el) => { el.textContent = countSource.textContent; });
        }
      });
      document.dispatchEvent(new CustomEvent('cart:updated'));
    }
  };
  window.VCCart = Cart;

  /* ---------- Drawer (menu + cart) ---------- */

  class VCDrawer extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-drawer-close]')) this.close();
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
      });
    }

    open(opener) {
      this.opener = opener || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      lockScroll(true);
      const focusTarget = this.querySelector('[data-drawer-focus]') || this.querySelector('button, a, input');
      if (focusTarget) setTimeout(() => focusTarget.focus(), 50);
    }

    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      lockScroll(false);
      if (this.opener && this.opener.focus) this.opener.focus();
    }
  }
  customElements.define('vc-drawer', VCDrawer);

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-drawer-open]');
    if (!trigger) return;
    const drawer = document.getElementById(trigger.dataset.drawerOpen);
    if (!drawer) return;
    event.preventDefault();
    drawer.open(trigger);
  });

  /* ---------- Cart items (drawer + cart page) ---------- */

  class VCCartItems extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-cart-change]');
        if (!button) return;
        event.preventDefault();
        this.update(Number(button.dataset.line), Number(button.dataset.quantity), button);
      });
      this.addEventListener('change', (event) => {
        const input = event.target.closest('[data-cart-quantity]');
        if (!input) return;
        this.update(Number(input.dataset.line), Math.max(0, parseInt(input.value, 10) || 0), input);
      });
    }

    async update(line, quantity, control) {
      this.setAttribute('aria-busy', 'true');
      if (control) control.disabled = true;
      try {
        const cart = await Cart.change(line, quantity);
        Cart.render(cart.sections);
      } catch (error) {
        const target = this.querySelector('[data-cart-error]');
        if (target) target.textContent = error.message;
      } finally {
        this.removeAttribute('aria-busy');
        if (control) control.disabled = false;
      }
    }
  }
  customElements.define('vc-cart-items', VCCartItems);

  /* ---------- Product form with quantity tiers ---------- */

  class VCProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form[action*="/cart/add"]');
      if (!this.form) return;
      this.quantityInput = this.form.querySelector('input[name="quantity"]');
      this.variantInput = this.form.querySelector('[name="id"]');
      this.submitButton = this.form.querySelector('[type="submit"]');
      this.buttonPrice = this.querySelector('[data-button-price]');
      this.errorBox = this.querySelector('[data-form-error]');
      this.variants = JSON.parse(this.querySelector('[data-variants-json]')?.textContent || '[]');

      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-tier-input]')) this.onTierChange(event.target);
        if (event.target === this.variantInput) this.onVariantChange();
      });
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
      this.updatePrices();
    }

    get currentVariant() {
      const id = Number(this.variantInput?.value);
      return this.variants.find((variant) => variant.id === id) || this.variants[0];
    }

    get selectedTier() {
      return this.querySelector('[data-tier-input]:checked');
    }

    onTierChange(input) {
      if (this.quantityInput) this.quantityInput.value = input.dataset.quantity;
      this.updatePrices();
    }

    onVariantChange() {
      const variant = this.currentVariant;
      if (!variant) return;
      const url = new URL(window.location.href);
      if (this.dataset.updateUrl === 'true') {
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
      this.updatePrices();
    }

    /** Recalculate every tier price from the current variant price. */
    updatePrices() {
      const variant = this.currentVariant;
      if (!variant) return;
      this.querySelectorAll('[data-tier]').forEach((tier) => {
        const input = tier.querySelector('[data-tier-input]');
        const quantity = Number(input.dataset.quantity);
        const discount = Number(input.dataset.discount) || 0;
        const full = variant.price * quantity;
        const final = Math.round(full * (100 - discount) / 100);
        tier.querySelector('[data-tier-price]').textContent = formatMoney(final);
        const compare = tier.querySelector('[data-tier-compare]');
        if (compare) compare.textContent = discount > 0 ? formatMoney(full) : '';
        const saving = tier.querySelector('[data-tier-saving]');
        if (saving) saving.textContent = discount > 0 ? saving.dataset.template.replace('[amount]', formatMoney(full - final)) : '';
        input.dataset.finalPrice = final;
      });
      const tier = this.selectedTier;
      const price = tier ? Number(tier.dataset.finalPrice) : variant.price * Number(this.quantityInput?.value || 1);
      if (this.buttonPrice) this.buttonPrice.textContent = formatMoney(price);
      if (this.submitButton) {
        this.submitButton.disabled = !variant.available;
        const label = this.submitButton.querySelector('[data-button-label]');
        if (label) label.textContent = variant.available ? theme.strings.addToCart : theme.strings.soldOut;
      }
    }

    async onSubmit(event) {
      if (!window.fetch) return;
      event.preventDefault();
      if (this.submitButton.disabled) return;
      this.submitButton.classList.add('is-loading');
      if (this.errorBox) this.errorBox.textContent = '';
      try {
        const formData = new FormData(this.form);
        const item = { id: Number(formData.get('id')), quantity: Number(formData.get('quantity') || 1) };
        const properties = {};
        formData.forEach((value, key) => {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match && value) properties[match[1]] = value;
        });
        if (Object.keys(properties).length) item.properties = properties;
        const response = await Cart.add([item]);
        Cart.render(response.sections);
        const drawer = document.getElementById('CartDrawer');
        if (drawer && drawer.open) drawer.open(this.submitButton);
        else window.location.href = theme.routes.cart;
      } catch (error) {
        if (this.errorBox) this.errorBox.textContent = error.message;
      } finally {
        this.submitButton.classList.remove('is-loading');
      }
    }
  }
  customElements.define('vc-product-form', VCProductForm);

  /* ---------- Media gallery ---------- */

  class VCGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      this.slides = [...this.querySelectorAll('[data-gallery-slide]')];
      this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
      this.dots = [...this.querySelectorAll('[data-gallery-dot]')];
      if (!this.track || this.slides.length < 2) return;

      [...this.thumbs, ...this.dots].forEach((control) => {
        control.addEventListener('click', () => this.goTo(Number(control.dataset.index)));
      });

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.6) this.setActive(this.slides.indexOf(entry.target));
        });
      }, { root: this.track, threshold: [0.6] });
      this.slides.forEach((slide) => this.observer.observe(slide));
    }

    goTo(index) {
      const slide = this.slides[index];
      if (!slide) return;
      this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
      this.setActive(index);
    }

    setActive(index) {
      [this.thumbs, this.dots].forEach((list) => list.forEach((el, i) => el.setAttribute('aria-current', i === index ? 'true' : 'false')));
      this.slides.forEach((slide, i) => {
        if (i !== index) slide.querySelectorAll('video').forEach((video) => video.pause());
      });
    }
  }
  customElements.define('vc-gallery', VCGallery);

  /* ---------- Sticky buy bar (mobile) ---------- */

  class VCStickyBar extends HTMLElement {
    connectedCallback() {
      this.target = document.querySelector('[data-sticky-target]');
      const update = () => {
        let visible = window.scrollY > 500;
        if (this.target) {
          const rect = this.target.getBoundingClientRect();
          const inView = rect.top < window.innerHeight && rect.bottom > 0;
          visible = visible && !inView;
        }
        if (document.documentElement.classList.contains('has-drawer')) visible = false;
        this.classList.toggle('is-visible', visible);
      };
      window.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();

    }
  }
  customElements.define('vc-sticky-bar', VCStickyBar);

  /* ---------- Rotating announcement (mobile) ---------- */

  class VCRotator extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('[data-rotator-item]')];
      if (this.items.length === 0) return;
      this.index = 0;
      this.items[0].classList.add('is-active');
      if (this.items.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.timer = setInterval(() => {
        this.items[this.index].classList.remove('is-active');
        this.index = (this.index + 1) % this.items.length;
        this.items[this.index].classList.add('is-active');
      }, 4000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
  }
  customElements.define('vc-rotator', VCRotator);

  /* ---------- YouTube modal ---------- */

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-youtube-id]');
    if (!trigger) return;
    const modal = document.getElementById('VideoModal');
    if (!modal || typeof modal.showModal !== 'function') return;
    event.preventDefault();
    const frame = modal.querySelector('[data-video-frame]');
    const id = encodeURIComponent(trigger.dataset.youtubeId);
    frame.innerHTML = `<iframe src="https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&playsinline=1" title="${trigger.dataset.videoTitle || 'Video'}" allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowfullscreen></iframe>`;
    modal.showModal();
  });

  document.addEventListener('close', (event) => {
    if (event.target.id === 'VideoModal') event.target.querySelector('[data-video-frame]').innerHTML = '';
  }, true);

  document.addEventListener('click', (event) => {
    const modal = event.target.closest('#VideoModal');
    if (modal && (event.target === modal || event.target.closest('[data-video-close]'))) modal.close();
  });

  /* ---------- Buy links ----------
     Links marked with data-buy-link point to the product page. If the current
     page already has a buy box, scroll to it instead of leaving the page. */

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[data-buy-link]');
    if (!link) return;
    const buyBox = document.querySelector('[data-buy-box]');
    if (!buyBox) return;
    event.preventDefault();
    const drawer = link.closest('vc-drawer');
    if (drawer) drawer.close();
    buyBox.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  /* ---------- Auto-submit selects (language switcher) ---------- */

  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-auto-submit]')) event.target.form.submit();
  });
})();
